import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Database, Loader2, RefreshCw, ShieldAlert } from "lucide-react";

import { ApiError, http } from "@/shared/api/http";
import { Button } from "@/shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/ui/card";
import FounderBillingMigrationApply from "./FounderBillingMigrationApply";

type ProjectionPagination = {
  page: number;
  page_size: number;
  total_pages: number;
  total_owners: number;
  total_workspaces: number;
  page_owners: number;
  page_workspaces: number;
  has_previous: boolean;
  has_next: boolean;
};

type ProjectionHygiene = {
  active_workspaces: number;
  projected_workspaces: number;
  excluded_workspaces: number;
  workspace_reconciliation: Record<string, number>;
  projected_owner_evidence: Record<string, number>;
  evidence_scope_sha256: Record<string, string>;
  hygiene_sha256: string;
  reconciles: boolean;
  labels_are_evidence_only: boolean;
};

type FounderIdentityAttestation = {
  configured_owners: number;
  verified_owners: number;
  unattested_projected_owners: number;
  verified_scope_sha256: string;
  unattested_scope_sha256: string;
  attestation_sha256: string;
  attestation_complete: boolean;
  labels_are_founder_supplied: boolean;
  records_are_not_modified: boolean;
};

type FounderMigrationScope = {
  approved_owners: number;
  approved_workspaces: number;
  excluded_projected_owners: number;
  owner_labels: string[];
  expected_scope_digest: string;
  projection_sha256: string;
  scope_lock_sha256: string;
  all_approved_owners_attested: boolean;
  records_are_not_modified: boolean;
  apply_authorized: boolean;
};

type FounderMigrationDryRun = {
  certified_target_match: boolean;
  certified_scope_match: boolean;
  approved_owners: number;
  approved_workspaces: number;
  expected_scope_digest?: string;
  projection_sha256?: string;
  scope_lock_sha256?: string;
  billing_account_upserts?: number;
  billing_accounts_existing?: number;
  workspace_links_to_write?: number;
  workspace_links_already_correct?: number;
  subscription_documents?: number;
  subscription_links_to_write?: number;
  subscription_links_already_correct?: number;
  owner_plans: Array<{
    owner_label: string;
    plan_code: string;
    status: string;
    billing_account_upsert: boolean;
    billing_account_exists: boolean;
    workspace_links_total: number;
    workspace_links_to_write: number;
    workspace_links_already_correct: number;
    subscription_links_total: number;
    subscription_links_to_write: number;
    subscription_links_already_correct: number;
  }>;
  records_are_not_modified: boolean;
  dry_run_only: boolean;
  apply_authorized: boolean;
  action_plan_sha256?: string;
  dry_run_sha256?: string;
  blocker?: string;
};

type FounderCommercialScopeDryRun = {
  certified_target_match: boolean;
  commercial_scope_match: boolean;
  canonical_billing_accounts: number;
  included_workspaces: number;
  excluded_test_workspaces: number;
  commercial_scope_sha256?: string;
  included_workspace_labels?: string[];
  excluded_workspace_labels?: string[];
  current_owner_split?: Record<string, number>;
  target_plan_code?: string;
  target_status?: string;
  billing_account_upserts?: number;
  billing_account_exists?: boolean;
  workspace_links_to_write?: number;
  workspace_links_already_correct?: number;
  subscription_documents?: number;
  subscription_links_to_write?: number;
  subscription_links_already_correct?: number;
  workspace_owner_changes: number;
  workspace_owner_ids_preserved?: boolean;
  authentication_ownership_changes?: number;
  legacy_workspace_plan_metadata_changes?: number;
  stripe_network_calls?: number;
  legacy_c9_action_plan_apply_eligible?: boolean;
  records_are_not_modified: boolean;
  dry_run_only: boolean;
  apply_authorized: boolean;
  action_plan_sha256?: string;
  dry_run_sha256?: string;
  blocker?: string;
};

type FounderMigrationPreservationCensus = {
  source: string;
  certified_target_match: boolean;
  commercial_scope_match: boolean;
  commercial_scope_sha256?: string;
  c91_action_plan_sha256?: string;
  c91_dry_run_sha256?: string;
  usage_period?: string;
  legacy_usage_rows_present?: boolean;
  legacy_usage_nonzero?: boolean;
  canonical_ba_usage_row_present?: boolean;
  canonical_ba_usage_nonzero?: boolean;
  active_legacy_reservations?: number;
  active_canonical_ba_reservations?: number;
  active_other_scope_reservations?: number;
  legacy_recurring_addons_present?: boolean;
  historical_invoices_total?: number;
  historical_invoices_already_canonical?: number;
  historical_invoices_without_billing_account?: number;
  historical_invoices_with_different_billing_account?: number;
  historical_invoices_need_linkage?: boolean;
  subscription_documents?: number;
  subscription_links_to_write?: number;
  subscription_links_already_correct?: number;
  subscription_state_matches_c91?: boolean;
  simple_apply_preservation_safe: boolean;
  nonempty_preservation_categories?: string[];
  records_are_not_modified: boolean;
  census_only: boolean;
  apply_authorized: boolean;
  preservation_census_sha256?: string;
  blocker?: string;
};

type ProductionAccountProjection = Record<string, unknown> & {
  pagination: ProjectionPagination;
  hygiene: ProjectionHygiene;
  founder_identity: FounderIdentityAttestation;
  founder_migration_scope: FounderMigrationScope;
  founder_migration_dry_run: FounderMigrationDryRun;
  founder_commercial_scope_dry_run: FounderCommercialScopeDryRun;
  founder_migration_preservation_census: FounderMigrationPreservationCensus;
};

async function loadProductionAccountProjection(page: number): Promise<ProductionAccountProjection> {
  return http<ProductionAccountProjection>(
    `/billing/admin/production-account-projection?page=${page}`,
    { method: "GET" },
  );
}

export default function ProductionAccountProjectionPage(): JSX.Element {
  const [page, setPage] = useState(1);
  const projection = useQuery<ProductionAccountProjection, ApiError>({
    queryKey: ["founder", "production-account-projection", page],
    queryFn: () => loadProductionAccountProjection(page),
    retry: false,
    staleTime: 0,
  });

  return (
    <div className="mx-auto max-w-5xl space-y-8 p-6" data-testid="production-account-projection-page">
      <header className="space-y-2">
        <div className="flex items-center gap-3">
          <Database className="h-6 w-6 text-primary" aria-hidden />
          <h1 className="text-3xl font-semibold tracking-tight">Production Billing Account Projection</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Founder-only, read-only migration planning. The historical projection below cannot apply a migration or modify billing data. The separate C.9.2B control above is the only write-capable surface and requires an exact confirmation phrase, certified digests, and the physical Production target.
        </p>
      </header>

      <FounderBillingMigrationApply />

      {projection.isLoading ? (
        <Card data-testid="projection-loading">
          <CardContent className="flex items-center gap-3 p-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Loading the read-only projection…
          </CardContent>
        </Card>
      ) : null}

      {projection.isError ? (
        <ProjectionError error={projection.error} onRetry={() => void projection.refetch()} />
      ) : null}

      {projection.isSuccess ? (
        <Card data-testid="projection-ready">
          <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0">
            <CardTitle>Sanitized projection</CardTitle>
            <Button variant="outline" size="sm" onClick={() => void projection.refetch()}>
              <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
              Refresh
            </Button>
          </CardHeader>
          <CardContent>
            <div className="mb-4 grid gap-3 sm:grid-cols-3" data-testid="projection-hygiene-summary">
              <SummaryMetric label="Active workspaces" value={projection.data.hygiene.active_workspaces} />
              <SummaryMetric label="Projected workspaces" value={projection.data.hygiene.projected_workspaces} />
              <SummaryMetric label="Excluded workspaces" value={projection.data.hygiene.excluded_workspaces} />
            </div>
            <div className="mb-4 rounded-lg border p-4 text-sm" data-testid="projection-hygiene-reconciliation">
              <p className="font-medium">Read-only hygiene evidence</p>
              <p className="mt-1 text-muted-foreground">
                Projected {projection.data.hygiene.workspace_reconciliation.projected ?? 0} · Ownerless {projection.data.hygiene.workspace_reconciliation.ownerless ?? 0} · Missing owner user {projection.data.hygiene.workspace_reconciliation.missing_owner_user ?? 0} · Public demo {projection.data.hygiene.workspace_reconciliation.public_demo_owner ?? 0} · Unexplained {projection.data.hygiene.workspace_reconciliation.unexplained_unprojected ?? 0}
              </p>
              <p className="mt-2 text-muted-foreground">
                Projected-owner evidence: Known test {projection.data.hygiene.projected_owner_evidence.known_test_evidence ?? 0} · Mixed {projection.data.hygiene.projected_owner_evidence.mixed_test_evidence ?? 0} · No test evidence {projection.data.hygiene.projected_owner_evidence.no_test_evidence ?? 0}
              </p>
            </div>
            <div className="mb-4 rounded-lg border p-4 text-sm" data-testid="projection-founder-identity-attestation">
              <p className="font-medium">Founder identity attestation</p>
              <p className="mt-1 text-muted-foreground">
                Verified {projection.data.founder_identity.verified_owners} of {projection.data.founder_identity.configured_owners} approved owners · Unattested projected owners {projection.data.founder_identity.unattested_projected_owners}
              </p>
              <p className="mt-2 text-muted-foreground">
                Read-only identity evidence. No records were modified and unattested owners remain unresolved.
              </p>
            </div>
            <div className="mb-4 rounded-lg border p-4 text-sm" data-testid="projection-founder-migration-scope">
              <p className="font-medium">Founder-approved migration scope lock</p>
              <p className="mt-1 text-muted-foreground">
                Approved {projection.data.founder_migration_scope.approved_owners} owners · {projection.data.founder_migration_scope.approved_workspaces} workspaces · Excluded projected owners {projection.data.founder_migration_scope.excluded_projected_owners}
              </p>
              <p className="mt-2 text-muted-foreground">
                Scope is digest-locked and read-only. Apply is not authorized from this screen.
              </p>
            </div>
            <div className="mb-4 rounded-lg border p-4 text-sm" data-testid="projection-founder-migration-dry-run">
              <p className="font-medium">Digest-locked migration dry-run</p>
              <p className="mt-1 text-muted-foreground">
                Certified target {projection.data.founder_migration_dry_run.certified_target_match ? "matched" : "blocked"} · Certified scope {projection.data.founder_migration_dry_run.certified_scope_match ? "matched" : "blocked"}
              </p>
              <p className="mt-2 text-muted-foreground">
                Planned account upserts {projection.data.founder_migration_dry_run.billing_account_upserts ?? 0} · Workspace links to write {projection.data.founder_migration_dry_run.workspace_links_to_write ?? 0} · Subscription links to write {projection.data.founder_migration_dry_run.subscription_links_to_write ?? 0}
              </p>
              <p className="mt-2 text-muted-foreground">
                Dry-run only. No records were modified. Apply remains unauthorized until a separate Founder-approved patch.
              </p>
              {projection.data.founder_migration_dry_run.action_plan_sha256 ? (
                <div className="mt-3 space-y-1 break-all font-mono text-xs text-muted-foreground">
                  <p>action_plan_sha256: {projection.data.founder_migration_dry_run.action_plan_sha256}</p>
                  <p>dry_run_sha256: {projection.data.founder_migration_dry_run.dry_run_sha256}</p>
                </div>
              ) : null}
              <p className="mt-3 text-muted-foreground">
                Legacy C.9 evidence only. These digests are superseded for apply by the corrected Founder commercial scope below.
              </p>
            </div>
            <div className="mb-4 rounded-lg border p-4 text-sm" data-testid="projection-founder-commercial-scope-dry-run">
              <p className="font-medium">Corrected Founder four-workspace dry-run</p>
              <p className="mt-1 text-muted-foreground">
                Certified target {projection.data.founder_commercial_scope_dry_run.certified_target_match ? "matched" : "blocked"} · Commercial scope {projection.data.founder_commercial_scope_dry_run.commercial_scope_match ? "matched" : "blocked"}
              </p>
              <p className="mt-2 text-muted-foreground">
                Canonical Billing Accounts {projection.data.founder_commercial_scope_dry_run.canonical_billing_accounts} · Included real workspaces {projection.data.founder_commercial_scope_dry_run.included_workspaces} · Excluded test workspaces {projection.data.founder_commercial_scope_dry_run.excluded_test_workspaces}
              </p>
              <p className="mt-2 text-muted-foreground">
                Target plan {projection.data.founder_commercial_scope_dry_run.target_plan_code ?? "blocked"} / {projection.data.founder_commercial_scope_dry_run.target_status ?? "blocked"} · Workspace links to write {projection.data.founder_commercial_scope_dry_run.workspace_links_to_write ?? 0} · Subscription links to write {projection.data.founder_commercial_scope_dry_run.subscription_links_to_write ?? 0}
              </p>
              <p className="mt-2 text-muted-foreground">
                Workspace owner changes {projection.data.founder_commercial_scope_dry_run.workspace_owner_changes} · Authentication ownership changes {projection.data.founder_commercial_scope_dry_run.authentication_ownership_changes ?? 0} · Stripe network calls {projection.data.founder_commercial_scope_dry_run.stripe_network_calls ?? 0}
              </p>
              <p className="mt-2 text-muted-foreground">
                Dry-run only. Existing workspace ownership is preserved. No records were modified and apply remains unauthorized.
              </p>
              {projection.data.founder_commercial_scope_dry_run.action_plan_sha256 ? (
                <div className="mt-3 space-y-1 break-all font-mono text-xs text-muted-foreground">
                  <p>commercial_scope_sha256: {projection.data.founder_commercial_scope_dry_run.commercial_scope_sha256}</p>
                  <p>corrected_action_plan_sha256: {projection.data.founder_commercial_scope_dry_run.action_plan_sha256}</p>
                  <p>corrected_dry_run_sha256: {projection.data.founder_commercial_scope_dry_run.dry_run_sha256}</p>
                </div>
              ) : null}
            </div>
            <div className="mb-4 rounded-lg border p-4 text-sm" data-testid="projection-founder-migration-preservation-census">
              <p className="font-medium">C.9.2A migration preservation census</p>
              <p className="mt-1 text-muted-foreground">
                Certified target {projection.data.founder_migration_preservation_census.certified_target_match ? "matched" : "blocked"} · Commercial scope {projection.data.founder_migration_preservation_census.commercial_scope_match ? "matched" : "blocked"}
              </p>
              <p className="mt-2 text-muted-foreground">
                Simple apply preservation safe {projection.data.founder_migration_preservation_census.simple_apply_preservation_safe ? "yes" : "no"} · Usage period {projection.data.founder_migration_preservation_census.usage_period ?? "blocked"}
              </p>
              <p className="mt-2 text-muted-foreground">
                Legacy usage nonzero {projection.data.founder_migration_preservation_census.legacy_usage_nonzero ? "yes" : "no"} · Active legacy reservations {projection.data.founder_migration_preservation_census.active_legacy_reservations ?? 0} · Legacy recurring add-ons {projection.data.founder_migration_preservation_census.legacy_recurring_addons_present ? "yes" : "no"}
              </p>
              <p className="mt-2 text-muted-foreground">
                Historical invoices needing linkage {projection.data.founder_migration_preservation_census.historical_invoices_need_linkage ? "yes" : "no"} · Subscription links to write {projection.data.founder_migration_preservation_census.subscription_links_to_write ?? 0} · Subscription state {projection.data.founder_migration_preservation_census.subscription_state_matches_c91 ? "matched" : "drifted"}
              </p>
              <p className="mt-2 text-muted-foreground">
                Census only. No records were modified. Production migration remains unauthorized.
              </p>
              {projection.data.founder_migration_preservation_census.preservation_census_sha256 ? (
                <div className="mt-3 space-y-1 break-all font-mono text-xs text-muted-foreground">
                  <p>preservation_census_sha256: {projection.data.founder_migration_preservation_census.preservation_census_sha256}</p>
                </div>
              ) : null}
              {projection.data.founder_migration_preservation_census.nonempty_preservation_categories?.length ? (
                <p className="mt-3 text-muted-foreground">
                  Preservation categories: {projection.data.founder_migration_preservation_census.nonempty_preservation_categories.join(", ")}
                </p>
              ) : null}
            </div>
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 text-sm" data-testid="projection-pagination">
              <span>
                Page {projection.data.pagination.page} of {projection.data.pagination.total_pages} · {projection.data.pagination.total_owners} owners · {projection.data.pagination.total_workspaces} workspaces
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!projection.data.pagination.has_previous || projection.isFetching}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!projection.data.pagination.has_next || projection.isFetching}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
            <pre className="max-h-[65vh] overflow-auto rounded-lg bg-muted p-4 text-xs leading-relaxed" data-testid="projection-json">
              {JSON.stringify(projection.data, null, 2)}
            </pre>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function SummaryMetric({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div className="rounded-lg border p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold">{value}</p>
    </div>
  );
}

function ProjectionError({ error, onRetry }: { error: ApiError; onRetry: () => void }): JSX.Element {
  const denied = error.status === 401 || error.status === 403;
  return (
    <Card className="border-amber-300" data-testid={denied ? "projection-forbidden" : "projection-error"}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-amber-700">
          <ShieldAlert className="h-5 w-5" aria-hidden />
          {denied ? "Founder access required" : "Projection unavailable"}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4 text-sm text-muted-foreground">
        <p>{denied ? "This read-only report is available only to an authenticated Founder." : error.message}</p>
        {!denied ? (
          <Button variant="outline" onClick={onRetry}>
            <RefreshCw className="mr-2 h-4 w-4" aria-hidden />
            Retry
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
}
